import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { getReadOnly } from '../../selectors/permissions';
import Shelf from '../../components/widgets/Shelf';
import Spinner from '../../components/widgets/Spinner';
import TextField from '../../components/molecules/TextField';
import Datepicker from '../../components/widgets/Datepicker';
import TagInput from '../../components/widgets/TagInput';
import WarningMessage from '../../components/utilities/WarningMessage';
import Button from '../../components/atoms/Button';
import GeneralPopup, { PopupTypes } from '../../components/organisms/GeneralPopup';
import PermissionsWrapper from '../../containers/PermissionsWrapper';
import { deleteGoal, fetchGoalDetails, fetchGoalFilters, GoalConflictError, saveGoal } from '../service';
import {
    buildEntries,
    buildSavePayload,
    computeTotalBudget,
    formatDigits,
    getDefaultGoalSpan,
    hydrateEntryValues,
    hydrateSelection,
    parseEntryValue,
    trackGoalsEvent,
} from '../utils';
import { DUPLICATE_NAME_PREFIX, SAVE_ACTIONS, SHELF_MODES, TRANSLATION_KEY } from '../constants';
import css from '../style.css';

const SHELF_HEADERS = {
    [SHELF_MODES.CREATE]: 'STATIC.PAGE_HEADERS.NEW_GOAL',
    [SHELF_MODES.EDIT]: 'STATIC.PAGE_HEADERS.EDIT_GOAL',
    [SHELF_MODES.DUPLICATE]: 'STATIC.PAGE_HEADERS.DUPLICATE_GOAL',
};

const ERASED_WARNING_TIMEOUT_MS = 6000;

const toTagInputTags = tags => tags.map(tag => ({ value: tag.id, label: tag.name }));

const stableStringify = value =>
    JSON.stringify(value, (key, val) =>
        val && typeof val === 'object' && !Array.isArray(val)
            ? Object.keys(val)
                  .sort()
                  .reduce((sorted, objectKey) => {
                      sorted[objectKey] = val[objectKey];
                      return sorted;
                  }, {})
            : val
    );

const formSnapshot = (name, dates, selection, entryValues) => stableStringify([name, dates, selection, entryValues]);

const GoalShelf = ({
    open,
    mode = SHELF_MODES.CREATE,
    goalId = null,
    currencySymbol = '',
    onClose,
    onSaved,
    translate = () => {},
}) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [availableFilters, setAvailableFilters] = useState([]);
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState(false);
    const [dates, setDates] = useState(getDefaultGoalSpan());
    const [selection, setSelection] = useState({});
    const [entryValues, setEntryValues] = useState({});
    const [setAllValue, setSetAllValue] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loadError, setLoadError] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showDiscardDialog, setShowDiscardDialog] = useState(false);
    const [showErasedWarning, setShowErasedWarning] = useState(false);

    const initialSnapshotRef = useRef('');
    const errorRef = useRef(null);

    const readOnly = useSelector(getReadOnly);
    const isEdit = mode === SHELF_MODES.EDIT;

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        let cancelled = false;
        const defaultSpan = getDefaultGoalSpan();
        setLoading(true);
        setLoadError(false);
        setName('');
        setNameError(false);
        setDates(defaultSpan);
        setSelection({});
        setEntryValues({});
        setSetAllValue('');
        setErrorMessage('');
        setShowDeleteDialog(false);
        setShowDiscardDialog(false);
        setShowErasedWarning(false);
        initialSnapshotRef.current = formSnapshot('', defaultSpan, {}, {});

        const requests = [fetchGoalFilters(), goalId ? fetchGoalDetails(goalId) : Promise.resolve(null)];
        Promise.all(requests)
            .then(([filters, details]) => {
                if (cancelled) {
                    return;
                }
                setAvailableFilters(filters);
                if (details) {
                    const loadedName =
                        mode === SHELF_MODES.DUPLICATE ? `${DUPLICATE_NAME_PREFIX}${details.name}` : details.name;
                    const loadedDates = { startDate: details.start_date, endDate: details.end_date };
                    const loadedSelection = hydrateSelection(filters, details.filters);
                    const loadedValues = hydrateEntryValues(details.entries);
                    setName(loadedName);
                    setDates(loadedDates);
                    setSelection(loadedSelection);
                    setEntryValues(loadedValues);
                    initialSnapshotRef.current = formSnapshot(loadedName, loadedDates, loadedSelection, loadedValues);
                }
                trackGoalsEvent('Goals: Definition: Page View', {
                    params: { Create: !isEdit, Edit: isEdit },
                    goal_id: isEdit ? goalId : null,
                });
            })
            .catch(error => {
                if (cancelled) {
                    return;
                }
                console.error('Failed to load goal data', error);
                setLoadError(true);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, goalId, mode]);

    const { entries, truncated, isTotalBudgetOnly } = useMemo(() => buildEntries(availableFilters, selection), [
        availableFilters,
        selection,
    ]);
    const totalBudget = useMemo(() => computeTotalBudget(entries, entryValues), [entries, entryValues]);
    const selectedFilters = useMemo(
        () =>
            Object.keys(selection)
                .sort()
                .map(filterName => availableFilters.find(filter => filter.name === filterName))
                .filter(Boolean),
        [selection, availableFilters]
    );

    // Warn transiently when a filter change removed sub goals that already had values.
    const prevEntriesRef = useRef(entries);
    useEffect(() => {
        const prevEntries = prevEntriesRef.current;
        prevEntriesRef.current = entries;
        if (loading || !open) {
            return undefined;
        }
        const newKeys = new Set(entries.map(entry => entry.key));
        const erased = prevEntries.some(entry => !newKeys.has(entry.key) && Number(entryValues[entry.key]));
        if (!erased) {
            return undefined;
        }
        setShowErasedWarning(true);
        const timer = setTimeout(() => setShowErasedWarning(false), ERASED_WARNING_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [entries]);

    // Surface validation/conflict errors even when the entries list is long.
    useEffect(() => {
        if (errorMessage && errorRef.current && typeof errorRef.current.scrollIntoView === 'function') {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [errorMessage]);

    const isDirty = () => formSnapshot(name, dates, selection, entryValues) !== initialSnapshotRef.current;

    // Only fired from the discard-dialog accept - closing a pristine shelf is not a
    // discarded edit.
    const discardAndClose = () => {
        setShowDiscardDialog(false);
        trackGoalsEvent(isEdit ? 'Goal Edit Discarded' : 'Goal Create Discarded', { goal_id: goalId });
        onClose();
    };

    const requestClose = () => {
        if (saving || deleting) {
            return;
        }
        if (!loading && !loadError && isDirty()) {
            setShowDiscardDialog(true);
            return;
        }
        onClose();
    };

    const toggleBreakdown = filter => {
        setSelection(prev => {
            const next = { ...prev };
            if (next[filter.name]) {
                trackGoalsEvent('Goal Filter Deselected', { filter_name: filter.name });
                delete next[filter.name];
            } else {
                trackGoalsEvent('Goal Filter Selected', { filter_name: filter.name });
                next[filter.name] = [];
            }
            return next;
        });
    };

    const setFilterTags = (filterName, tags) => {
        setSelection(prev => ({ ...prev, [filterName]: tags }));
    };

    const handleEntryValueChange = (entryKey, value) => {
        setEntryValues(prev => ({ ...prev, [entryKey]: parseEntryValue(value) }));
    };

    const applySetAll = () => {
        const value = parseEntryValue(setAllValue);
        setEntryValues(prev => {
            const next = { ...prev };
            entries.forEach(entry => {
                next[entry.key] = value;
            });
            return next;
        });
        trackGoalsEvent('Goals: Definition: Event', { value, set_all: true });
    };

    const handleSave = () => {
        if (readOnly) {
            return;
        }

        setErrorMessage('');
        const trimmedName = name.trim();
        setNameError(!trimmedName);

        if (!trimmedName) {
            return;
        }

        if (selectedFilters.some(filter => !(selection[filter.name] || []).length)) {
            setErrorMessage(translate(`${TRANSLATION_KEY}.VALIDATION_EMPTY_BREAKDOWNS`));
            return;
        }

        if (!entries.some(entry => Number(entryValues[entry.key]))) {
            setErrorMessage(translate(`${TRANSLATION_KEY}.VALIDATION_NO_VALUES`));
            return;
        }

        setSaving(true);
        const payload = buildSavePayload({
            goalId: isEdit ? goalId : null,
            name: trimmedName,
            startDate: dates.startDate,
            endDate: dates.endDate,
            availableFilters,
            selection,
            entries,
            entryValues,
        });
        saveGoal(payload)
            .then(() => {
                trackGoalsEvent(isEdit ? 'Goal Edit Saved' : 'New Goal Created', {
                    goal_id: payload.id,
                    goal_name: payload.name,
                    goal_metric: payload.metric,
                    goal_condition: payload.condition,
                    goal_scope: payload.scope,
                    start_date: payload.start_date,
                    end_date: payload.end_date,
                    breakdown: Object.keys(selection),
                });
                onSaved(isEdit ? SAVE_ACTIONS.SAVED : SAVE_ACTIONS.CREATED);
            })
            .catch(error => {
                if (error instanceof GoalConflictError) {
                    setErrorMessage(error.message);
                } else {
                    console.error('Failed to save goal', error);
                    setErrorMessage(translate(`${TRANSLATION_KEY}.SAVE_ERROR`));
                }
            })
            .finally(() => setSaving(false));
    };

    const handleDelete = () => {
        setDeleting(true);
        deleteGoal(goalId)
            .then(goalName => {
                trackGoalsEvent('Goal Deleted', { goal_id: goalId, goal_name: goalName });
                setShowDeleteDialog(false);
                onSaved(SAVE_ACTIONS.DELETED);
            })
            .catch(error => {
                console.error('Failed to delete goal', error);
                setShowDeleteDialog(false);
                setErrorMessage(translate(`${TRANSLATION_KEY}.DELETE_ERROR`));
            })
            .finally(() => setDeleting(false));
    };

    return (
        <Shelf
            open={open}
            headerText={SHELF_HEADERS[mode]}
            onClose={requestClose}
            shelfSize="large"
            dataTestId="goal-shelf"
            footerNavigationButtons={[
                { type: 'cancelButton', text: 'STATIC.BUTTONS.CANCEL', disabled: saving || deleting },
                {
                    type: 'primaryButton',
                    text: 'STATIC.BUTTONS.SAVE',
                    onClick: handleSave,
                    showSpinner: saving,
                    disabled: loading || loadError || saving || deleting || readOnly,
                },
            ]}
        >
            {loading ? (
                <Spinner show expanded />
            ) : (
                <div className={css.shelfContent}>
                    {loadError ? (
                        <WarningMessage type="error" message={`${TRANSLATION_KEY}.LOAD_ERROR`} />
                    ) : (
                        <>
                            <div className={css.formRow}>
                                <div className={css.formLabel}>
                                    <Translate id="STATIC.PAGES.NEW_GOAL.GOAL_NAME" />
                                </div>
                                <div className={css.formField}>
                                    <TextField
                                        value={name}
                                        onChange={value => {
                                            setName(value);
                                            if (value.trim()) {
                                                setNameError(false);
                                            }
                                        }}
                                        error={
                                            nameError ? translate(`${TRANSLATION_KEY}.VALIDATION_NAME_REQUIRED`) : null
                                        }
                                        inputConfig={{ 'data-testid': 'goal-name' }}
                                    />
                                </div>
                            </div>

                            <div className={css.formRow}>
                                <div className={css.formLabel}>
                                    <Translate id="STATIC.PAGES.NEW_GOAL.DURATION" />
                                </div>
                                <div className={css.formField}>
                                    <Datepicker
                                        startDate={dates.startDate}
                                        endDate={dates.endDate}
                                        onApply={({ start_date: startDate, end_date: endDate }) =>
                                            setDates({ startDate, endDate })
                                        }
                                        ranges={{}}
                                        dateLimit={{ years: 5 }}
                                    />
                                </div>
                            </div>

                            <div className={css.formRow}>
                                <div className={css.formLabel}>
                                    <Translate id="STATIC.PAGES.NEW_GOAL.BREAKDOWNS_TITLE" />
                                </div>
                                <div className={css.breakdownButtons}>
                                    {availableFilters.map(filter => {
                                        const selectedCount = (selection[filter.name] || []).length;
                                        return (
                                            <button
                                                type="button"
                                                key={filter.name}
                                                className={
                                                    selection[filter.name]
                                                        ? css.breakdownButtonSelected
                                                        : css.breakdownButton
                                                }
                                                onClick={() => toggleBreakdown(filter)}
                                            >
                                                {filter.display_name}
                                                {selectedCount ? ` (${selectedCount})` : ''}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <WarningMessage
                                show={showErasedWarning}
                                type="warning"
                                message={`${TRANSLATION_KEY}.VALUES_ERASED_ALERT`}
                                className={css.shelfWarning}
                            />

                            {!!selectedFilters.length && (
                                <div className={css.formRow}>
                                    <div className={css.formLabel}>
                                        <Translate id="STATIC.FILTERS" />
                                    </div>
                                    <div className={css.filterRows}>
                                        {selectedFilters.map(filter => (
                                            <div key={filter.name} className={css.filterRow}>
                                                <div className={css.filterName}>{filter.display_name}</div>
                                                <div className={css.filterOperator}>
                                                    <Translate id={`${TRANSLATION_KEY}.INCLUDES`} />
                                                </div>
                                                <div className={css.filterTags}>
                                                    <TagInput
                                                        tags={toTagInputTags(selection[filter.name] || [])}
                                                        suggestions={toTagInputTags(filter.list)}
                                                        onChange={tags =>
                                                            setFilterTags(
                                                                filter.name,
                                                                (tags || []).map(tag => ({
                                                                    id: tag.value,
                                                                    name: tag.label,
                                                                }))
                                                            )
                                                        }
                                                        placeholder={`${TRANSLATION_KEY}.FILTER_MANDATORY`}
                                                        dataTestId={`goal-filter-${filter.name}`}
                                                    />
                                                    <div className={css.filterBulkActions}>
                                                        <button
                                                            type="button"
                                                            className={css.filterBulkAction}
                                                            onClick={() =>
                                                                setFilterTags(filter.name, [...filter.list])
                                                            }
                                                        >
                                                            <Translate id={`${TRANSLATION_KEY}.SELECT_ALL`} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={css.filterBulkAction}
                                                            disabled={!(selection[filter.name] || []).length}
                                                            onClick={() => setFilterTags(filter.name, [])}
                                                        >
                                                            <Translate id={`${TRANSLATION_KEY}.CLEAR_ALL`} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <WarningMessage
                                show={truncated}
                                type="warning"
                                message={`${TRANSLATION_KEY}.MAX_ENTRIES_ALERT`}
                                className={css.shelfWarning}
                            />

                            <div className={css.subGoalsSection}>
                                <div className={css.subGoalsHeader}>
                                    <div className={css.formLabel}>
                                        <Translate id="STATIC.PAGES.NEW_GOAL.SUB_GOALS" />
                                    </div>
                                    {!isTotalBudgetOnly && (
                                        <div className={css.setAllContainer}>
                                            <input
                                                className={css.entryInput}
                                                value={formatDigits(setAllValue)}
                                                placeholder={translate(`${TRANSLATION_KEY}.SET_ALL_PLACEHOLDER`)}
                                                onChange={event => setSetAllValue(parseEntryValue(event.target.value))}
                                                aria-label={translate(`${TRANSLATION_KEY}.SET_ALL`)}
                                            />
                                            <Button type="secondary" disabled={!setAllValue} onClick={applySetAll}>
                                                <Translate id={`${TRANSLATION_KEY}.SET_ALL`} />
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {entries.map(entry => (
                                    <div key={entry.key} className={css.entryRow}>
                                        <div className={css.entryLabel}>{entry.label}</div>
                                        <div className={css.entryValue}>
                                            <span className={css.entryCurrency}>{currencySymbol}</span>
                                            <input
                                                className={css.entryInput}
                                                value={formatDigits(entryValues[entry.key] || '')}
                                                onChange={event =>
                                                    handleEntryValueChange(entry.key, event.target.value)
                                                }
                                                aria-label={entry.label}
                                            />
                                        </div>
                                    </div>
                                ))}

                                {!isTotalBudgetOnly && (
                                    <div className={css.totalBudgetRow}>
                                        <span className={css.totalBudgetLabel}>
                                            <Translate id="STATIC.PAGES.NEW_GOAL.TOTAL_BUDGET" />
                                        </span>
                                        <span className={css.totalBudgetValue}>
                                            {currencySymbol} <strong>{formatDigits(String(totalBudget)) || '0'}</strong>
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div ref={errorRef}>
                                <WarningMessage
                                    show={!!errorMessage}
                                    type="error"
                                    message={<span>{errorMessage}</span>}
                                    className={css.shelfWarning}
                                />
                            </div>

                            {isEdit && (
                                <PermissionsWrapper write>
                                    <div className={css.deleteContainer}>
                                        <Button
                                            type="attention"
                                            text="STATIC.BUTTONS.DELETE"
                                            onClick={() => setShowDeleteDialog(true)}
                                            disabled={saving || deleting}
                                        />
                                    </div>
                                </PermissionsWrapper>
                            )}
                        </>
                    )}
                </div>
            )}

            <GeneralPopup
                open={showDeleteDialog}
                type={PopupTypes.DELETE}
                title={`${TRANSLATION_KEY}.DELETE_MODAL.TITLE`}
                text={`${TRANSLATION_KEY}.DELETE_MODAL.MESSAGE`}
                acceptText={`${TRANSLATION_KEY}.DELETE_MODAL.ACCEPT_BUTTON_TEXT`}
                rejectText={`${TRANSLATION_KEY}.DELETE_MODAL.REJECT_BUTTON_TEXT`}
                onAccept={handleDelete}
                onReject={() => setShowDeleteDialog(false)}
                showSpinner={deleting}
            />

            <GeneralPopup
                open={showDiscardDialog}
                type={PopupTypes.WARNING_WITH_CANCEL}
                title={`${TRANSLATION_KEY}.DISCARD_MODAL.TITLE`}
                text={`${TRANSLATION_KEY}.DISCARD_MODAL.MESSAGE`}
                acceptText={`${TRANSLATION_KEY}.DISCARD_MODAL.ACCEPT_BUTTON_TEXT`}
                rejectText={`${TRANSLATION_KEY}.DISCARD_MODAL.REJECT_BUTTON_TEXT`}
                onAccept={discardAndClose}
                onReject={() => setShowDiscardDialog(false)}
            />
        </Shelf>
    );
};

GoalShelf.propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(Object.values(SHELF_MODES)),
    goalId: PropTypes.string,
    currencySymbol: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
    translate: PropTypes.func,
};

export default GoalShelf;
